import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { CartService } from '../../services/cart.service';
import { OnlineProduct, ProductAttribute } from '../../interfaces/product';
import { WishlistService } from '../../services/wishlist.service';
import { CompareService } from '../../services/compare.service';
import { QuickviewService } from '../../services/quickview.service';
import { RootService } from '../../services/root.service';
import { CurrencyService } from '../../services/currency.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { UserService } from '../../services/user.service';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { CurrencyFormatOptions } from '../../interfaces/currency-format-options';
import { AnalyticsList, AnalyticsService } from '../../services/analytics.service';
import { CountryGateService } from '../../services/country-gate.service';
import { CardPriceService } from '../../services/card-price.service';
import { BRAND, SITE_ORIGIN } from 'src/app/shared/brand';
import { CultureService } from '../../culture/culture.service';

@Component({
    selector: 'app-product-card',
    templateUrl: './product-card.component.html',
    styleUrls: ['./product-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCardComponent implements OnInit, OnDestroy, OnChanges {
    /// The brand, for the template (shared/brand.ts).
    readonly brand = BRAND;

    private destroy$: Subject<void> = new Subject();

    @Input() product: OnlineProduct;
    @Input() layout: 'grid-sm'|'grid-nl'|'grid-lg'|'list'|'horizontal'|null = null;
    @Input() hideBrand = false;
    @Input() isSlider = false;

    /// The list this card is shown in and its position there, for GA4's list reports. A card
    /// placed without them still reports the click, just not where it came from.
    @Input() list: AnalyticsList = null;
    @Input() index: number = null;

    addingToCart = false;
    addingToWishlist = false;
    addingToCompare = false;
    showingQuickview = false;
    featuredAttributes: ProductAttribute[] = [];
    userInfo:any = {};
    isMyProduct:boolean;
    sellAt:number = 45;
    currencyOptions:CurrencyFormatOptions;

    /// The owner seeing the shop from where they are is listed every product; on one their own
    /// country cannot buy, this names that country, so they know a shopper there never sees it.
    /// Empty for everyone else - their lists hold only what their country can buy - and for the
    /// owner seeing the shop as another country, who is shown exactly what a shopper there is.
    notSoldIn = '';

    /// What the card shows, in US dollars: the listing's own figures, until the visitor's country has its own - the ones
    /// the product page opens on there (CardPriceService): the small-fee surcharge in the price, another warehouse's price.
    price = 0;
    regularPrice = 0;
    discount = 0;
    private priceSub: Subscription = null;

    constructor(
        private cd: ChangeDetectorRef,
        public root: RootService,
        public cart: CartService,
        public wishlist: WishlistService,
        public compare: CompareService,
        public quickview: QuickviewService, 
        public currency:CurrencyService,
        private userService:UserService,
        private router:Router,
        private analytics:AnalyticsService,
        private gate:CountryGateService,
        private cardPrices:CardPriceService,
        private labels:CultureService,
         @Inject(PLATFORM_ID) private platformId: any,
    ) { }

    /// Opening the product from this card, by its image, its name or quick view.
    selected(): void
    {
        this.analytics.selectItem(this.product, this.list, this.index);
    }

    ngOnInit(): void 
    { 
        this.currencyOptions = this.currency.options;

        this.currency.changes$.pipe(takeUntil(this.destroy$)).subscribe(res =>{ 
            if(res){
                this.currencyOptions = res;
            }
        })
        
        this.userService.onUserInfoChanged$.pipe(takeUntil(this.destroy$)).subscribe(res =>
        { 
            this.isMyProduct = false; 
            this.userInfo = res; 
           
            if(this.userInfo !== null && this.userInfo.Id && this.userInfo.Id === this.product.SUID)
            { 
                this.isMyProduct = true; 
            }
            this.notSoldIn = this.notSoldInMyCountry();
            this.cd.markForCheck();
        });

        this.currency.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.cd.markForCheck();
        });
    }

    ngOnDestroy(): void 
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void 
    {
        if ('product' in changes) {
          //  this.featuredAttributes = !this.product ? [] : this.product.attributes.filter(x => x.featured);
            this.notSoldIn = this.notSoldInMyCountry();
            this.showPrice(null);
            this.askPrice();
        }
    }

    /// The visitor's figures where their country has them, the listing's own otherwise.
    private showPrice(theirs:{ Price:number, RegularPrice:number, Discount:number }): void
    {
        const shown = theirs || this.product;
        this.price = shown ? shown.Price : 0;
        this.regularPrice = shown ? shown.RegularPrice : 0;
        this.discount = shown ? shown.Discount : 0;
    }

    private askPrice(): void
    {
        if (this.priceSub) { this.priceSub.unsubscribe(); }
        this.priceSub = this.cardPrices.priceFor(this.product).pipe(takeUntil(this.destroy$)).subscribe(theirs =>
        {
            this.showPrice(theirs);
            this.cd.markForCheck();
        });
    }

    private notSoldInMyCountry(): string
    {
        if (!this.product || !this.gate.isAdmin() || this.gate.previewing()) { return ''; }
        const country = this.gate.country();
        return this.gate.isBlocked(this.product.BlockedCountryCodes, country) ? country : '';
    }
 
    addToCart(): void 
    {
        if (this.addingToCart) 
        {
            return;
        }

        this.addingToCart = true;
        this.quickview.show(this.product).subscribe({
            complete: () => {
                this.addingToCart = false;
                this.cd.markForCheck();
            }
        });

        // if (this.addingToCart) 
        // {
        //     return;
        // }
        // this.addingToCart = true; 

       
        // this.cart.add(this.product, 1, this.product.OptionList[0].items[0]).subscribe({
        //     complete: () => 
        //     {
        //         this.addingToCart = false;
        //         this.cd.markForCheck(); 
        //     }
        // });
    }

    addToWishlist(): void 
    {
        if (this.addingToWishlist) {
            return;
        }

        this.addingToWishlist = true;
        this.wishlist.add(this.product).subscribe({
            complete: () => {
                this.addingToWishlist = false;
                this.cd.markForCheck();
            }
        });
    }

    addToCompare(): void 
    {
        if (this.addingToCompare) {
            return;
        }

        this.addingToCompare = true;
        this.compare.add(this.product).subscribe({
            complete: () => {
                this.addingToCompare = false;
                this.cd.markForCheck();
            }
        });
    }

    showQuickview(): void 
    {
        if (this.showingQuickview) 
        {
            return;
        }

        this.showingQuickview = true;
        this.selected();
        this.quickview.show(this.product).subscribe({
            complete: () => {
                this.showingQuickview = false;
                this.cd.markForCheck();
            }
        });
    } 

    shareLink() {
        if (isPlatformBrowser(this.platformId) && navigator.share) {
          navigator.share({
            title: this.product.Name,
            text: this.labels.t('product-share-text', 'Check the product you might find interesting.'),
            url: SITE_ORIGIN + '/products/details/'+this.product.Slug
          })
          .then(() => console.log('Successful share'))
          .catch((error) => console.error('Error sharing', error));
        } else 
        {
          console.warn('Web Share API not supported or not running in browser.');
        }
    }
    
    editProduct()
    {
        // this.quickview.showAddEditProduct(this.product).subscribe({
        //     complete: () => {
        //         this.showingQuickview = false;
        //         this.cd.markForCheck();
        //     }
        // });

        this.router.navigate(['/account/productpublish/'+this.product.Slug]);
    }

    getFormattedPrice() {
        return this.currency.priceParts(this.price);
    }
}

